import { TFunction } from 'i18next';
import { getTranslationKey } from 'utils/localization';
import { TNamespaceEnum } from 'types/types';

/**
 * Maps `social.*` backend error codes to localized message keys (design §8).
 *
 * The IS2.Social API surfaces a small set of `social.*` error codes on
 * publish / connect / callback. Instead of a generic toast, the composer and
 * the OAuth callback resolve the code here to a concrete i18n key in the
 * `pages/social` namespace (`errors.<code>`). The i18n JSON itself is owned by a
 * parallel agent — these keys are the contract it fills in.
 *
 * The failure reason of a channel — `error_message` / `ig_error_message` of a
 * scheduled post, `error` of a publish-now channel — is a code too, optionally
 * followed by a detail: `social.<code>` or `social.<code>: <detail>`, the detail
 * usually being Meta's own (English) message. {@link describeSocialReason}
 * turns one into the text a club reads; anything that is not a known code (an
 * older row's English text, an automation reason written as a Czech sentence)
 * is shown as it was stored.
 */

/** The known `social.*` error codes (bare, without the `social.` prefix). */
export type SocialErrorCode =
    | 'no_managed_pages'
    | 'nothing_to_publish'
    | 'invalid_image_data'
    | 'connection_not_found'
    // publish-now / schedule name a missing or unusable connection this way.
    | 'no_connection_for_club'
    | 'club_access_denied'
    // Page picker of the connect flow.
    | 'page_selection_expired'
    | 'page_not_available'
    | 'page_cannot_publish'
    // Channel selection and Instagram (publish-now, schedule, automation rules).
    | 'no_channel_selected'
    | 'instagram_not_connected'
    | 'instagram_requires_image'
    | 'instagram_requires_jpeg'
    | 'instagram_caption_too_long'
    | 'instagram_too_many_hashtags'
    | 'instagram_too_many_mentions'
    | 'instagram_not_configured'
    | 'instagram_container_failed'
    | 'instagram_container_timeout'
    // Instagram's own limits, checked before (or reported by) the publish.
    | 'instagram_rate_limited'
    | 'instagram_image_too_large'
    | 'instagram_image_aspect_ratio'
    // A connection whose token expired; publish-now refuses it (422).
    | 'connection_needs_reconnect'
    // The club's own Instagram connection (Instagram Login).
    | 'instagram_needs_reconnect'
    | 'instagram_login_not_configured'
    | 'instagram_not_professional'
    | 'instagram_connect_failed'
    // Per-channel failure reasons of a publish (stored on the post, or the
    // `error` of a publish-now channel), mostly with Meta's message as detail.
    | 'facebook_publish_failed'
    | 'facebook_returned_no_post_id'
    | 'instagram_publish_failed'
    | 'not_attempted'
    | 'unknown_error'
    | 'publish_outcome_unknown'
    | 'scheduled_post_image_missing';

/** Prefix the backend uses for this error family, e.g. `social.no_managed_pages`. */
export const SOCIAL_ERROR_PREFIX = 'social.';

/**
 * `social.*` code -> i18n message key (`pages/social:errors.<code>`). Built via
 * {@link getTranslationKey} so it stays consistent with the rest of the app's
 * namespaced-key convention.
 */
export const SOCIAL_ERROR_MESSAGE_KEYS: Record<SocialErrorCode, string> = {
    no_managed_pages: getTranslationKey(
        TNamespaceEnum.social,
        'errors.no_managed_pages'
    ),
    nothing_to_publish: getTranslationKey(
        TNamespaceEnum.social,
        'errors.nothing_to_publish'
    ),
    invalid_image_data: getTranslationKey(
        TNamespaceEnum.social,
        'errors.invalid_image_data'
    ),
    connection_not_found: getTranslationKey(
        TNamespaceEnum.social,
        'errors.connection_not_found'
    ),
    // Same situation the old publish endpoint reported as connection_not_found.
    no_connection_for_club: getTranslationKey(
        TNamespaceEnum.social,
        'errors.no_connection_for_club'
    ),
    club_access_denied: getTranslationKey(
        TNamespaceEnum.social,
        'errors.club_access_denied'
    ),
    page_selection_expired: getTranslationKey(
        TNamespaceEnum.social,
        'errors.page_selection_expired'
    ),
    page_not_available: getTranslationKey(
        TNamespaceEnum.social,
        'errors.page_not_available'
    ),
    page_cannot_publish: getTranslationKey(
        TNamespaceEnum.social,
        'errors.page_cannot_publish'
    ),
    no_channel_selected: getTranslationKey(
        TNamespaceEnum.social,
        'errors.no_channel_selected'
    ),
    instagram_not_connected: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_not_connected'
    ),
    instagram_requires_image: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_requires_image'
    ),
    instagram_requires_jpeg: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_requires_jpeg'
    ),
    instagram_caption_too_long: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_caption_too_long'
    ),
    instagram_too_many_hashtags: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_too_many_hashtags'
    ),
    instagram_too_many_mentions: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_too_many_mentions'
    ),
    instagram_not_configured: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_not_configured'
    ),
    instagram_container_failed: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_container_failed'
    ),
    instagram_container_timeout: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_container_timeout'
    ),
    instagram_rate_limited: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_rate_limited'
    ),
    instagram_image_too_large: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_image_too_large'
    ),
    instagram_image_aspect_ratio: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_image_aspect_ratio'
    ),
    connection_needs_reconnect: getTranslationKey(
        TNamespaceEnum.social,
        'errors.connection_needs_reconnect'
    ),
    instagram_needs_reconnect: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_needs_reconnect'
    ),
    instagram_login_not_configured: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_login_not_configured'
    ),
    instagram_not_professional: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_not_professional'
    ),
    instagram_connect_failed: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_connect_failed'
    ),
    facebook_publish_failed: getTranslationKey(
        TNamespaceEnum.social,
        'errors.facebook_publish_failed'
    ),
    facebook_returned_no_post_id: getTranslationKey(
        TNamespaceEnum.social,
        'errors.facebook_returned_no_post_id'
    ),
    instagram_publish_failed: getTranslationKey(
        TNamespaceEnum.social,
        'errors.instagram_publish_failed'
    ),
    not_attempted: getTranslationKey(
        TNamespaceEnum.social,
        'errors.not_attempted'
    ),
    unknown_error: getTranslationKey(
        TNamespaceEnum.social,
        'errors.unknown_error'
    ),
    publish_outcome_unknown: getTranslationKey(
        TNamespaceEnum.social,
        'errors.publish_outcome_unknown'
    ),
    scheduled_post_image_missing: getTranslationKey(
        TNamespaceEnum.social,
        'errors.scheduled_post_image_missing'
    ),
};

/** Type guard: is `code` one of the known bare `SocialErrorCode`s? */
const isSocialErrorCode = (code: string): code is SocialErrorCode =>
    Object.prototype.hasOwnProperty.call(SOCIAL_ERROR_MESSAGE_KEYS, code);

/**
 * Reasons the backend stored as plain English before they became codes; the
 * rows that still carry them read like the codes that replaced them.
 */
const LEGACY_REASONS: Record<string, SocialErrorCode> = {
    'not attempted': 'not_attempted',
    'unknown error': 'unknown_error',
};

/** A failure reason split into its known code and its detail. */
export interface SocialReason {
    /** The known bare code, or `null` when the reason is free text. */
    code: SocialErrorCode | null;
    /**
     * Whatever follows the code (usually Meta's message), or — for free text —
     * the whole reason; `null` when there is nothing besides the code.
     */
    detail: string | null;
}

/**
 * Splits a raw reason (`social.instagram_publish_failed: Media ID is not
 * available`) into `{ code, detail }`. Unknown codes and free text come back
 * as `{ code: null, detail: <the trimmed text> }`; an empty reason as `null`.
 */
export const parseSocialReason = (
    raw: string | null | undefined
): SocialReason | null => {
    const text = typeof raw === 'string' ? raw.trim() : '';

    if (!text) {
        return null;
    }

    const legacy = LEGACY_REASONS[text.toLowerCase()];

    if (legacy) {
        return { code: legacy, detail: null };
    }

    // `social.<code>` or the bare `<code>`, optionally followed by
    // `": <detail>"` — split at the first colon, since Meta's message may
    // contain more. Only an exact known code counts, so no English or Czech
    // sentence is ever mistaken for one.
    const separator = text.indexOf(':');
    const head = separator === -1 ? text : text.slice(0, separator);
    const code = head.startsWith(SOCIAL_ERROR_PREFIX)
        ? head.slice(SOCIAL_ERROR_PREFIX.length)
        : head;

    if (isSocialErrorCode(code)) {
        return {
            code,
            detail:
                separator === -1
                    ? null
                    : text.slice(separator + 1).trim() || null,
        };
    }

    return { code: null, detail: text };
};

/**
 * The text a club reads for a failure reason: the code's own message, with the
 * detail in parentheses when there is one — "Instagram příspěvek odmítl.
 * (Media ID is not available)". A reason that is not a known code is returned
 * unchanged, since it is the only diagnosis there is. `null` for no reason.
 */
export const describeSocialReason = (
    t: TFunction,
    raw: string | null | undefined
): string | null => {
    const reason = parseSocialReason(raw);

    if (!reason) {
        return null;
    }

    if (!reason.code) {
        return reason.detail;
    }

    const message = t(SOCIAL_ERROR_MESSAGE_KEYS[reason.code]) as string;

    return reason.detail ? `${message} (${reason.detail})` : message;
};

/**
 * Resolves a raw backend error code (`social.no_managed_pages` or the bare
 * `no_managed_pages`, also with a `": <detail>"` tail) to its i18n message key,
 * or returns `fallback` (typically a generic-error key) when the code is
 * unknown, empty, or undefined.
 */
export const messageKeyForSocialError = (
    code: string | null | undefined,
    fallback: string
): string => {
    const reason = parseSocialReason(code);

    return reason?.code ? SOCIAL_ERROR_MESSAGE_KEYS[reason.code] : fallback;
};

/**
 * Rejection of a Social service call that produced no result — thrown by
 * `SocialService.publishNow` for a 422 validation error, an auth failure or a
 * timeout.
 *
 * `code` is the raw backend code (`social.instagram_requires_jpeg`, …) or
 * `'no_code'` when there was none. It is also the error's `message`, so both
 * `messageKeyForSocialError(error.code, fallback)` and a generic
 * `catch (e) { …(e as Error).message… }` resolve to the right i18n key.
 */
export class SocialApiError extends Error {
    readonly code: string;

    /** HTTP status of the failed call, when the fetcher reported one (422, …). */
    readonly status?: number;

    /** The backend's `errors` array, verbatim. */
    readonly errors?: unknown;

    constructor(code: string, status?: number, errors?: unknown) {
        super(code);
        this.name = 'SocialApiError';
        this.code = code;
        this.status = status;
        this.errors = errors;
        // Babel compiles classes down for older browsers, and a down-compiled
        // subclass of Error loses its prototype — without this `instanceof
        // SocialApiError` would be false for the very errors thrown here.
        Object.setPrototypeOf(this, SocialApiError.prototype);
    }
}

/** Type guard that also survives a lost prototype (checks the name). */
export const isSocialApiError = (error: unknown): error is SocialApiError =>
    error instanceof SocialApiError ||
    (error instanceof Error && error.name === 'SocialApiError');

/**
 * Pulls the backend error code out of whatever a failed Social call produced:
 * a bare string, a `FormResponseData`-like `{ error }` (string or list), or an
 * axios-style body with `errorKey` / `message`. Returns `null` when there is
 * nothing usable, so callers fall back to the generic message.
 */
export const extractSocialErrorCode = (err: unknown): string | null => {
    if (!err) {
        return null;
    }

    if (typeof err === 'string') {
        return err;
    }

    const candidate = err as {
        error?: string | string[];
        message?: string;
        errorKey?: string;
    };

    if (Array.isArray(candidate.error)) {
        return candidate.error[0];
    }

    return candidate.error || candidate.errorKey || candidate.message || null;
};
